import type { 
  FreeTrialRequest, 
  FreeTrialResponse, 
  FreeTrialStatus, 
  FreeTrialUsageRequest, 
  FreeTrialUsageResponse 
} from '@/services/interfaces/FreeTrial';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export class FreeTrialService {
  /**
   * 开始免费试用
   */
  static async startFreeTrial(request: FreeTrialRequest): Promise<FreeTrialResponse> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/free-trial/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        if (response.status !== 404) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to start free trial:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * 获取免费试用状态
   */
  static async getFreeTrialStatus(trialId: string): Promise<FreeTrialStatus | null> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/free-trial/status/${trialId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        trialId: data.trialId,
        remainingTokens: data.remainingTokens,
        expiresAt: data.expiresAt,
        isActive: data.isActive,
        isExpired: data.isExpired,
      };
    } catch (error) {
      console.error('Failed to get free trial status:', error);
      return null;
    }
  }

  /**
   * 更新token使用量
   */
  static async updateTokenUsage(request: FreeTrialUsageRequest): Promise<FreeTrialUsageResponse> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/free-trial/usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update token usage:', error);
      return {
        success: false,
        remainingTokens: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * 从localStorage获取已保存的试用ID
   */
  static getSavedTrialId(): string | null {
    return localStorage.getItem('free_trial_id');
  }

  /**
   * 保存试用ID到localStorage
   */
  static saveTrialId(trialId: string): void {
    localStorage.setItem('free_trial_id', trialId);
  }

  /**
   * 清除保存的试用ID
   */
  static clearSavedTrialId(): void {
    localStorage.removeItem('free_trial_id');
  }
} 